import { useThemeContext } from '#contexts/ThemeContext';
import { CallToAction, EPaymentState } from '#models/outcome';
import { EPaymentType, Price, Product } from '#models/product';
import { ECallToActionType } from '#models/questionnaire';
import { handleClick } from '#modules/Outcome/utils/callToAction';
import { Box, Divider, Grid, makeStyles, Button as MuiButton, Typography } from '@material-ui/core';
import { YouTube } from '@material-ui/icons';
// import { useCurrencyStorage } from "#queries/questionnaire";
import { usePaymentContext } from '#contexts/PaymentContext';
import { useBreakpoints } from '#utils/breakpoint';
import { toProductCurrency } from '#utils/product';
import { priceBoxShadow } from '#utils/style';
import { boxShadow, colors } from '@swyft/ui';
import { sortBy } from 'lodash-es';
import React from 'react';

const useStyles = () => {
  const { primaryButtonBackground, primaryButtonText, priceText, productHighlightsText } = useThemeContext();
  return makeStyles((theme: any) => ({
    textColor: {
      color: priceText
    },
    textButton: {
      color: primaryButtonText
    },
    button: {
      backgroundColor: primaryButtonBackground,
      color: primaryButtonText,
      boxShadow: boxShadow,
      '&:focus': {
        backgroundColor: primaryButtonBackground
      },
      '&:hover': {
        backgroundColor: primaryButtonBackground
      }
    },
    customDivider: {
      borderBottom: `1px dashed ${colors.grey[100]}`,
      background: 'unset'
    },
    priceRange: {
      color: productHighlightsText
    }
  }))();
};

export const Actions = ({
  price,
  callToActions = [],
  priceImpact = 0,
  discountValue = 0,
  videoUrl = '',
  trackCTAs,
  product
}: {
  price: Price[];
  callToActions: CallToAction[];
  priceImpact?: number;
  discountValue?: number;
  videoUrl?: string;
  trackCTAs: (cta: CallToAction) => void;
  product: Product;
}) => {
  const classes = useStyles();
  const { isXl } = useBreakpoints();
  const { currency } = useThemeContext();
  const onePrice = price?.find((item) => item.paymentType === EPaymentType.ONE);
  const monthlyPrice = price?.find((item) => item.paymentType === EPaymentType.MONTHLY);
  const { minimumPrice = null, maximumPrice = null, monthlyMinimumPrice = null, monthlyMaximumPrice = null } = product;
  const isStaticPriceRange = minimumPrice !== null && maximumPrice !== null;
  const isMonthlyPriceRange = monthlyMinimumPrice !== null && monthlyMaximumPrice !== null;
  const isShowPriceRange = isStaticPriceRange || isMonthlyPriceRange;
  const {
    state: { dataPayment },
    setPaymentProduct,
    setDataPayment,
    setActiveState,
    setCurrentPageState,
    paymentEnabled,
    schedulingEnabled
  } = usePaymentContext();

  const handleCTA = (item: CallToAction) => {
    trackCTAs(item);
    if (item.type === ECallToActionType.Payment && paymentEnabled) {
      setPaymentProduct(product);
      setDataPayment({ ...dataPayment, price: (onePrice?.price || 0) + priceImpact - discountValue });
      setActiveState(EPaymentState.Payment);
      setCurrentPageState(EPaymentState.Payment);
      return;
    }
    if (item.type === ECallToActionType.Schedule && schedulingEnabled) {
      setPaymentProduct(product);
      setDataPayment({ ...dataPayment, price: (onePrice?.price || 0) + priceImpact - discountValue });
      setActiveState(EPaymentState.Scheduling);
      setCurrentPageState(EPaymentState.Scheduling);
      return;
    }
    handleClick(item, videoUrl);
  };

  return (
    <Box>
      {!isShowPriceRange && (
        <React.Fragment>
          {(onePrice || !!priceImpact) && (
            <React.Fragment>
              <Typography variant='subtitle1' className={classes.textColor}>
                Starting from
              </Typography>
              <Typography variant='h5' className={`${classes.textColor} semiBold`}>
                {currency &&
                  toProductCurrency(((onePrice && onePrice.price) || 0) + priceImpact - discountValue, currency.name)}
              </Typography>
              {!!discountValue && (
                <Typography variant='subtitle1' className={classes.textColor}>
                  Including a discount of {currency && toProductCurrency(discountValue, currency.name)}
                </Typography>
              )}
            </React.Fragment>
          )}
          {(onePrice || !!priceImpact) && monthlyPrice && (
            <Typography variant='subtitle2' className={classes.textColor}>
              OR
            </Typography>
          )}
          {monthlyPrice && (
            <Box display='flex' alignItems='baseline'>
              <Typography variant='subtitle2' className={classes.textColor}>
                from
              </Typography>
              <Box mx={0.5}>
                <Typography variant='h5' className={`${classes.textColor} semiBold`}>
                  {currency && toProductCurrency(monthlyPrice.price, currency.name)}
                </Typography>
              </Box>
              <Typography variant='subtitle2' className={classes.textColor}>
                per month
              </Typography>
            </Box>
          )}
        </React.Fragment>
      )}
      {isShowPriceRange && (
        <Box
          border={`2px solid ${colors.grey[50]}`}
          borderRadius={8}
          p={2}
          textAlign='left'
          width={{ xs: '100%', sm: '90%', md: '100%' }}
          maxWidth={{ xs: '100%', sm: 350 }}
          mx={{ xs: 'auto', lg: 0 }}
          boxShadow={priceBoxShadow}
          bgcolor={colors.white}
        >
          <Typography variant='body2' gutterBottom className={classes.priceRange}>
            Price Range
          </Typography>
          <Divider />
          <Box display='flex' mt={1}>
            <Box display='flex' flexDirection='column'>
              {isStaticPriceRange && (
                <Typography variant='h6' className={`${classes.textColor} semiBold`}>
                  {currency && toProductCurrency((minimumPrice || 0) + priceImpact, currency.name)}
                </Typography>
              )}
              {isMonthlyPriceRange && (
                <Typography
                  variant={!isStaticPriceRange ? 'h6' : 'caption'}
                  className={`${classes.textColor} ${!isStaticPriceRange ? 'semiBold' : ''}`}
                >
                  {currency && toProductCurrency(monthlyMinimumPrice || 0, currency.name)} p/m
                </Typography>
              )}
            </Box>
            <Box mx={2} className={`${classes.textColor} semiBold`}>
              {'-'}
            </Box>
            <Box display='flex' flexDirection='column'>
              {isStaticPriceRange && (
                <Typography variant='h6' className={`${classes.textColor} semiBold`}>
                  {currency && toProductCurrency((maximumPrice || 0) + priceImpact, currency.name)}
                </Typography>
              )}
              {isMonthlyPriceRange && (
                <Typography
                  variant={!isStaticPriceRange ? 'h6' : 'caption'}
                  className={`${classes.textColor} ${!isStaticPriceRange ? 'semiBold' : ''}`}
                >
                  {currency && toProductCurrency(monthlyMaximumPrice || 0, currency.name)} p/m
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <Box mt={onePrice || monthlyPrice || isShowPriceRange || priceImpact ? 2 : 0}>
        <Grid container>
          {sortBy(
            callToActions,
            ({ type }) => (type === ECallToActionType.Schedule ? 0 : type === ECallToActionType.Payment ? 1 : 2),
            'type'
          ).map((item, index) =>
            !(
              (item.type === ECallToActionType.Phone && isXl) ||
              (item.type === ECallToActionType.Video && !videoUrl?.length) ||
              (item.type === ECallToActionType.Payment && !paymentEnabled) ||
              (item.type === ECallToActionType.Schedule && !schedulingEnabled)
            ) ? (
              <Grid key={index} xs={12} sm={6} md={9} lg={6} item>
                <Box display='flex' mt={2} maxWidth={{ xs: 200, md: 180 }} height={40}>
                  <MuiButton
                    onClick={() => handleCTA(item)}
                    className={`${classes.button} swyft-normal`}
                    fullWidth
                    size='large'
                    startIcon={item.type === ECallToActionType.Video ? <YouTube /> : null}
                  >
                    <Typography variant='subtitle2' className={`${classes.textButton} semiBold`}>
                      {item.title}
                    </Typography>
                  </MuiButton>
                </Box>
              </Grid>
            ) : null
          )}
        </Grid>
      </Box>
      <Box mt={2} mb={1}>
        <Divider variant='fullWidth' className={classes.customDivider} />
      </Box>
    </Box>
  );
};
